import { FormulaIssue, FunctionDescription, VariableOption } from "@/types/formula";

/**
 * Everything the formula inputs need to know about a source, as functions of strings.
 *
 * None of this validates: `POST /dialer/check-formula` is the only thing that says whether
 * a source is a formula, because the grammar, the catalogue and the type rules live in one
 * place and that place is the engine. What is here is the reading an editor needs between
 * two keystrokes — where the caret is, what to suggest, which characters to underline —
 * and it is separated out because it is the part worth testing without a DOM.
 */

/**
 * A source that is nothing but a variable path.
 *
 * Mirror of `formula.parser.is_plain_path`, and the same regular expression: a hyphen is
 * legal inside a segment, because every retry counter in the existing graphs has a
 * hyphenated step id. The mirror exists because the decision it drives — a picker or an
 * expression editor — is taken before any request, and asking the server which of its two
 * inputs to render would make opening a panel wait on a round trip.
 */
const PLAIN_PATH = /^[A-Za-z_][A-Za-z0-9_-]*(?:\.[A-Za-z0-9_-]+)*$/;

/** Words the grammar owns, which therefore are not paths however much they look like one. */
const RESERVED = new Set(["true", "false", "null", "and", "or", "not"]);

export function isPlainPath(source: string | null | undefined): boolean {
    const trimmed = (source ?? "").trim();
    if (!trimmed || !PLAIN_PATH.test(trimmed)) return false;
    return !RESERVED.has(trimmed.toLowerCase());
}

/**
 * What opens the value of a switchable field that holds an expression.
 *
 * Mirror of `dialer.dsl.formulas.FORMULA_MARK`, and the encoding the whole toggle rests
 * on: a switchable field holds one string, an expression when it opens with `=` and the
 * literal of its declared type otherwise. Doubled, it is a literal `=` — which is the
 * only reason the reading is total rather than nearly total, since `=8 la conclusion` is
 * a thing somebody types into a campaign name.
 */
export const FORMULA_MARK = "=";

/** Whether a switchable field's stored value holds an expression rather than a value. */
export function isMarked(value: unknown): boolean {
    return typeof value === "string" && value.startsWith(FORMULA_MARK) && !value.startsWith(FORMULA_MARK.repeat(2));
}

/** The expression inside a marked value, mark removed. */
export function sourceOf(value: string): string {
    return value.slice(FORMULA_MARK.length);
}

/** An expression as a switchable field stores it. */
export function marked(source: string): string {
    return `${FORMULA_MARK}${source}`;
}

/** The literal a switchable field holds, unescaped. */
export function literalOf(value: unknown): unknown {
    if (typeof value === "string" && value.startsWith(FORMULA_MARK.repeat(2))) return value.slice(FORMULA_MARK.length);
    return value;
}

/** A literal as a switchable field stores it: escaped when it opens with the mark. */
export function asLiteral(value: unknown): unknown {
    if (typeof value === "string" && value.startsWith(FORMULA_MARK)) return `${FORMULA_MARK}${value}`;
    return value;
}

/**
 * The source of the expression that yields this value.
 *
 * Mirror of `formula.parser.quote`, and the reason the conversion is not a mode flag: an
 * author clicks the button on a field holding `Bonjour Marie`, and unquoted that is two
 * variable names of nothing — the value lost, the publication blocked, and the lesson
 * learnt that the button breaks fields. The quoting rules are the language's, so this
 * function and its server twin have to agree; they are covered by tests on both sides.
 */
export function quote(value: unknown): string {
    if (value === null || value === undefined) return "null";
    if (typeof value === "boolean") return value ? "true" : "false";
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
    const text = typeof value === "string" ? value : (JSON.stringify(value) ?? String(value));
    return `'${text.replace(/\\/g, "\\\\").replace(/'/g, "\\'")}'`;
}

/** A single quoted text literal, with the two escapes the lexer understands and no others. */
const QUOTED_LITERAL = /^'((?:[^'\\]|\\['\\])*)'$/;
/**
 * The same literal in double quotes, which the lexer reads too (`formula.lexer._QUOTES`)
 * and which the first `set_variables` rows were written with: `"oKVdarDX"`.
 */
const DOUBLE_QUOTED_LITERAL = /^"((?:[^"\\]|\\["\\])*)"$/;
/** A number as the grammar spells one: digits, an optional fraction, no exponent. */
const NUMBER_LITERAL = /^-?\d+(?:\.\d+)?$/;

/**
 * `[true, value]` when a source is one literal, `[false, null]` when it computes.
 *
 * Mirror of `formula.parser.as_constant`, narrowed on purpose: the server answers from
 * the parse tree, this reads the three literal shapes off the source, and the difference
 * only shows on something like `(1)` — read here as a computation, whose source is then
 * kept as text. Rendering an input must not wait on a round trip, and the case where the
 * two disagree costs an author a pair of parentheses in a box they can see.
 */
export function asConstant(source: string): [boolean, unknown] {
    const trimmed = (source ?? "").trim();
    if (trimmed === "null") return [true, null];
    if (trimmed === "true") return [true, true];
    if (trimmed === "false") return [true, false];
    if (NUMBER_LITERAL.test(trimmed)) return [true, Number(trimmed)];
    const quoted = QUOTED_LITERAL.exec(trimmed);
    if (quoted) return [true, quoted[1].replace(/\\(['\\])/g, "$1")];
    const doubled = DOUBLE_QUOTED_LITERAL.exec(trimmed);
    if (doubled) return [true, doubled[1].replace(/\\(["\\])/g, "$1")];
    return [false, null];
}

/** The plain inputs a constant can be typed into, one per formula type worth one. */
export type ConstantKind = "text" | "number" | "boolean" | "date" | "datetime";

/** Which plain input a formula type gets. Anything without one of its own is text. */
export function constantKind(type: string | null | undefined): ConstantKind {
    if (type === "number" || type === "boolean" || type === "date" || type === "datetime") return type;
    return "text";
}

/**
 * The expression a plain input writes: `quote()` of the value read as its kind.
 *
 * Nothing typed stays nothing, so clearing the input clears the field instead of storing
 * `''`. A number that does not read as one is kept as text rather than dropped, and the
 * publication check then says what is wrong with it — the same rule as a declaration's
 * starting value.
 */
export function constantSource(value: unknown, kind: ConstantKind): string {
    if (value === null || value === undefined || value === "") return "";
    if (kind === "boolean") return quote(value === true || value === "true");
    if (kind === "number") {
        const number = typeof value === "number" ? value : Number(String(value).trim().replace(",", "."));
        return Number.isFinite(number) ? quote(number) : quote(String(value));
    }
    return quote(String(value));
}

/**
 * `[true, value]` when a stored expression can be shown in the plain input of `kind`.
 *
 * An empty source is a constant not yet typed. A literal of another kind is not shown —
 * text in a number input would be silently rewritten on the next keystroke — so it stays
 * in the formula editor, where it is read as written. Text takes any literal but `null`.
 */
export function constantOf(source: string | null | undefined, kind: ConstantKind): [boolean, unknown] {
    if (!(source ?? "").trim()) return [true, ""];
    const [constant, held] = asConstant(source as string);
    if (!constant || held === null) return [false, null];
    if (kind === "number") return typeof held === "number" ? [true, held] : [false, null];
    if (kind === "boolean") return typeof held === "boolean" ? [true, held] : [false, null];
    if (kind === "date" || kind === "datetime") return typeof held === "string" ? [true, held] : [false, null];
    return [true, held];
}

/** The same field, switched to the expression that yields what it already held. */
export function toFormula(value: unknown): string {
    return marked(quote(literalOf(value)));
}

/**
 * The same field, switched back to the value its expression was.
 *
 * A value comes back verbatim; a *computation* has no value until a card exists, so its
 * source is kept as the text of the literal. That is what makes the button preserving in
 * both directions: an author who converted by mistake reads their expression back instead
 * of an empty box.
 */
export function toLiteral(value: unknown): unknown {
    if (!isMarked(value)) return value;
    const source = sourceOf(value as string).trim();
    if (!source) return "";
    const [constant, held] = asConstant(source);
    return asLiteral(constant ? held : source);
}

/** Heading for the catalogue entries the server grouped under nothing. */
export const UNGROUPED = "Other";

/**
 * Heading for a path that is a way in rather than a value.
 *
 * A heading of its own because a container has no group: the catalogue groups *fields*, so
 * `snapshot` would otherwise inherit the heading of whichever field happens to be declared
 * first under it and read as « Contact » while holding ninety other things.
 */
export const CONTAINERS = "Groups of fields";

export type PathSuggestion = {
    /** What gets written into the field when this is picked. */
    path: string;
    /** The part of the path this entry adds, which is what the list shows in bold. */
    segment: string;
    label: string;
    type: string;
    group: string;
    description?: string | null;
    example?: string | null;
    /**
     * More segments follow: picking this appends a dot and keeps the list open, rather
     * than committing a path that reads a container.
     */
    continues: boolean;
};

/** The scope a query addresses — everything up to and including its last dot — and the rest. */
export function splitQuery(query: string): { scope: string; needle: string } {
    const raw = query ?? "";
    const cut = raw.lastIndexOf(".");
    const scope = cut === -1 ? "" : raw.slice(0, cut + 1);
    return { scope, needle: raw.slice(scope.length).trim() };
}

/**
 * What to offer for what has been typed so far.
 *
 * Three behaviours, and the difference between them is the whole reason this is not a
 * filtered list of four hundred paths:
 *
 * - **nothing typed**, at the root: the catalogue's own order, which puts the names an
 *   author should reach for first. The groups are then headings that make the list
 *   readable rather than a wall of `snapshot.…`;
 * - **nothing typed**, inside a scope: one segment deeper and no further, so
 *   `snapshot.` offers `contact` as a way in and not the forty fields behind it;
 * - **something typed**: a free search within the scope, offering the whole path, so
 *   typing « mobile » lands `snapshot.mobile_phone` in one pick instead of asking the
 *   author to walk down to it.
 *
 * A namespace is never expanded — nothing can enumerate one counter per step id — so it
 * is offered as a way in and the author types the rest. `namespaceFor` is how the caller
 * explains that instead of showing an empty list.
 */
export function suggestPaths(options: VariableOption[], query: string, { limit = 60 }: { limit?: number } = {}): PathSuggestion[] {
    const { scope, needle } = splitQuery(query);
    const lowered = needle.toLowerCase();
    const found: PathSuggestion[] = [];
    const seen = new Set<string>();

    for (const option of options) {
        if (scope && !option.value.startsWith(scope)) continue;
        const rest = option.value.slice(scope.length);
        if (!rest) continue;

        if (lowered) {
            const matches = rest.toLowerCase().includes(lowered) || (option.label ?? "").toLowerCase().startsWith(lowered);
            if (!matches) continue;
            if (seen.has(option.value)) continue;
            seen.add(option.value);
            found.push({
                path: option.value,
                segment: rest,
                label: option.label || option.value,
                type: option.type,
                group: option.group || UNGROUPED,
                description: option.description,
                example: option.example,
                continues: Boolean(option.is_namespace)
            });
        } else {
            const dot = rest.indexOf(".");
            const segment = dot === -1 ? rest : rest.slice(0, dot);
            const path = scope + segment;
            if (seen.has(path)) continue;
            seen.add(path);
            const deeper = dot !== -1;
            found.push({
                path,
                segment,
                // An intermediate segment has no label of its own: the option's label
                // describes a leaf below it, and printing « First name » beside `contact`
                // would be a lie. The segment is the honest heading for a way in.
                label: deeper ? segment : option.label || path,
                type: deeper ? "" : option.type,
                group: deeper ? CONTAINERS : option.group || UNGROUPED,
                description: deeper ? null : option.description,
                example: deeper ? null : option.example,
                continues: deeper || Boolean(option.is_namespace)
            });
        }

        if (found.length >= limit) break;
    }

    return found;
}

/**
 * The namespace a query has walked into, if any.
 *
 * What lets the picker say « any step id goes here » where it cannot list anything, using
 * the words and the example the catalogue already carries. The longest match wins, so a
 * namespace nested under another is described by itself.
 */
export function namespaceFor(options: VariableOption[], query: string): VariableOption | null {
    let best: VariableOption | null = null;
    for (const option of options) {
        if (!option.is_namespace) continue;
        if (!(query ?? "").startsWith(`${option.value}.`)) continue;
        if (!best || option.value.length > best.value.length) best = option;
    }
    return best;
}

export type SuggestionGroup = { group: string; items: PathSuggestion[] };

/**
 * Suggestions under their headings, groups in the order the catalogue introduced them.
 *
 * First appearance rather than alphabetical: the server puts the canonical names first
 * precisely so that the heading an author reads first is the one they want, and sorting
 * here would throw that away.
 */
export function groupSuggestions(items: PathSuggestion[]): SuggestionGroup[] {
    const groups: SuggestionGroup[] = [];
    const index = new Map<string, SuggestionGroup>();
    for (const item of items) {
        let group = index.get(item.group);
        if (!group) {
            group = { group: item.group, items: [] };
            index.set(item.group, group);
            groups.push(group);
        }
        group.items.push(item);
    }
    return groups;
}

/** Characters of a source one diagnostic is about. */
export type IssueSpan = { start: number; end: number };

/** Run of characters that make up one name, number or path, for widening a bare offset. */
const TOKEN_AT = /^[A-Za-z0-9_.\-]+/;

/**
 * Which characters an issue points at.
 *
 * The engine reports an offset and not a length, so the extent is reconstructed: the
 * `subject` when the source really carries it there — an unknown variable names itself —
 * and otherwise the token the offset falls on. Underlining a whole word rather than one
 * character is what makes the mark findable in a 400px column, and falling back on a
 * single character keeps the function total for an offset pointing at whitespace or past
 * the end, which is what a « never closed » diagnostic does.
 */
export function issueSpan(source: string, issue: FormulaIssue): IssueSpan {
    const start = Math.max(0, Math.min(issue.position ?? 0, source.length));
    const subject = issue.subject ?? "";
    if (subject && source.startsWith(subject, start)) {
        return { start, end: start + subject.length };
    }
    const token = TOKEN_AT.exec(source.slice(start));
    if (token) return { start, end: start + token[0].length };
    return { start, end: Math.min(start + 1, source.length) };
}

export type SourceSegment = { text: string; severity: "error" | "warning" | null };

/**
 * The source cut into runs, each carrying the worst severity that covers it.
 *
 * Produced as text rather than as absolute marks because the underline is drawn by a layer
 * mirroring the input's own wrapping: a source that wraps has no single line to put a mark
 * under, and measuring characters would break the moment the panel is resized.
 */
export function markSource(source: string, issues: FormulaIssue[]): SourceSegment[] {
    if (!source) return [];
    const severities: ("error" | "warning" | null)[] = new Array(source.length).fill(null);
    for (const issue of issues) {
        const { start, end } = issueSpan(source, issue);
        for (let index = start; index < end; index += 1) {
            // An error wins over a warning on the same characters: the author needs to see
            // what stops a publication, not what merely deserves a look.
            if (issue.severity === "error" || severities[index] === null) severities[index] = issue.severity;
        }
    }

    const segments: SourceSegment[] = [];
    for (let index = 0; index < source.length; index += 1) {
        const severity = severities[index];
        const last = segments[segments.length - 1];
        if (last && last.severity === severity) last.text += source[index];
        else segments.push({ text: source[index], severity });
    }
    return segments;
}

/** Operators of the grammar, in the order a palette reads best. */
export const FORMULA_OPERATORS = ["+", "-", "*", "/", "%", "=", "!=", ">", "<", ">=", "<=", "and", "or", "not"] as const;

export type Insertion = { source: string; caret: number };

/**
 * An operator written into a source, with the spaces that make it one.
 *
 * The spaces are not cosmetic and this is the one place they are guaranteed: a step id is
 * hyphenated, so `attempts.relance-1` is a *name* and `attempts.relance - 1` is a
 * subtraction. An author who types the minus themselves can still get it wrong — which is
 * why the rule is also written where they can read it — but an author who picks the
 * operator cannot.
 */
export function insertOperator(source: string, caret: number, operator: string): Insertion {
    const at = Math.max(0, Math.min(caret, source.length));
    const before = source.slice(0, at);
    const after = source.slice(at);
    const left = before.endsWith(" ") || !before ? before : `${before} `;
    const right = after.startsWith(" ") ? after : ` ${after}`;
    const written = `${left}${operator}${right}`;
    return { source: written, caret: left.length + operator.length + 1 };
}

/** A function of the vocabulary written in, caret left between its parentheses. */
export function insertFunction(source: string, caret: number, name: string): Insertion {
    const at = Math.max(0, Math.min(caret, source.length));
    const before = source.slice(0, at);
    const after = source.slice(at);
    const written = `${before}${name}()${after}`;
    return { source: written, caret: before.length + name.length + 1 };
}

/**
 * The path fragment being typed at the caret, in an expression.
 *
 * Read backwards from the caret over the characters a path may hold, which is what makes
 * the suggestions follow the caret instead of the whole field: in
 * `coalesce(snapshot.mobile_phone, snap` only the last word is being completed.
 */
export function pathFragmentAt(source: string, caret: number): { start: number; query: string } {
    const at = Math.max(0, Math.min(caret, source.length));
    let start = at;
    while (start > 0 && /[A-Za-z0-9_.\-]/.test(source[start - 1])) start -= 1;
    return { start, query: source.slice(start, at) };
}

/** A path written over the fragment the caret was in. */
export function replaceFragment(source: string, caret: number, path: string, { open }: { open: boolean }): Insertion {
    const { start } = pathFragmentAt(source, caret);
    const at = Math.max(0, Math.min(caret, source.length));
    const written = `${source.slice(0, start)}${path}${open ? "." : ""}${source.slice(at)}`;
    return { source: written, caret: start + path.length + (open ? 1 : 0) };
}

export type TemplateHole = { start: number; end: number; query: string };

const OPENING = "{{";
const CLOSING = "}}";

/**
 * The `{{ … }}` the caret sits in, or `null` when it sits in the literal text.
 *
 * This is what makes one component serve a template: suggestions are offered inside a
 * hole and nowhere else, so an author writing the body of an SMS is not interrupted by a
 * list of variables at every word.
 */
export function holeAt(source: string, caret: number): TemplateHole | null {
    const at = Math.max(0, Math.min(caret, source.length));
    const opening = source.lastIndexOf(OPENING, Math.max(0, at - 1));
    if (opening === -1) return null;
    const start = opening + OPENING.length;
    const closed = source.indexOf(CLOSING, start);
    if (closed !== -1 && closed < at) return null;
    if (start > at) return null;
    return { start, end: closed === -1 ? at : closed, query: source.slice(start, at).trim() };
}

/**
 * Whether the hole holds nothing but the path being completed.
 *
 * Then a pick rewrites the hole and closes it — `{{ snap` becomes `{{ snapshot.first_name }}`.
 * Otherwise the hole is an expression, `{{ concat('Hello ', snap`, and only the name under
 * the caret is replaced: rewriting the whole hole would throw away the call around it.
 */
export function holeIsPath(source: string, hole: TemplateHole, caret: number): boolean {
    const { start } = pathFragmentAt(source, caret);
    const before = source.slice(hole.start, Math.max(hole.start, start));
    const after = source.slice(Math.max(caret, hole.start), hole.end);
    return before.trim() === "" && /^[A-Za-z0-9_.-]*\s*$/.test(after);
}

/** A hole filled in, caret left after the closing brace. */
export function fillHole(source: string, hole: TemplateHole, path: string, { open }: { open: boolean }): Insertion {
    const inner = ` ${path}${open ? "." : ""} `;
    const closed = source.slice(hole.end).startsWith(CLOSING);
    const tail = closed ? source.slice(hole.end) : `${CLOSING}${source.slice(hole.end)}`;
    const written = `${source.slice(0, hole.start)}${inner}${tail}`;
    // Inside the braces when there is more to type, past them when the path is complete:
    // an author completing a namespace has a segment left to write.
    return { source: written, caret: open ? hole.start + inner.length - 1 : hole.start + inner.length + CLOSING.length };
}

/** An empty hole appended, ready for a variable. */
export function insertHole(source: string, caret: number): Insertion {
    const at = Math.max(0, Math.min(caret, source.length));
    const written = `${source.slice(0, at)}${OPENING}  ${CLOSING}${source.slice(at)}`;
    return { source: written, caret: at + OPENING.length + 1 };
}

/**
 * The expressions a template holds — what is between its `{{ }}` — one per line.
 *
 * The literal text around them is prose, where `Auxiliaire de vie-sociale` is not a name
 * anybody could misread as a subtraction; only the holes are written in the language.
 */
export function templateHoleSources(source: string): string {
    const found: string[] = [];
    let at = 0;
    for (;;) {
        const opening = source.indexOf(OPENING, at);
        if (opening === -1) break;
        const closing = source.indexOf(CLOSING, opening + OPENING.length);
        found.push(source.slice(opening + OPENING.length, closing === -1 ? undefined : closing));
        if (closing === -1) break;
        at = closing + CLOSING.length;
    }
    return found.join("\n");
}

/** Quoted text, whose contents are literal and therefore hold no names. */
const QUOTED = /'[^']*'|"[^"]*"/g;
const NAME = /[A-Za-z_][A-Za-z0-9_.\-]*/g;

/**
 * The hyphenated names a source holds, so the rule about them can be stated about the
 * source in front of the author rather than in general.
 *
 * `attempts.relance-1` is one name here and reads as a counter for a step called
 * `relance-1`; an author who meant « one fewer than the relance counter » wrote a name.
 * Quoted text is blanked first, because a hyphen inside a message is not an identifier.
 */
export function hyphenatedNames(source: string): string[] {
    const bare = (source ?? "").replace(QUOTED, match => " ".repeat(match.length));
    const found = new Set<string>();
    for (const match of bare.matchAll(NAME)) {
        if (match[0].includes("-")) found.add(match[0]);
    }
    return [...found];
}

/** The signature a palette prints for a function, from what the server described. */
export function functionSignature({ name, parameters, minimum, variadic }: { name: string; parameters: string[]; minimum: number; variadic: boolean }): string {
    const written = parameters.map((parameter, index) => (index < minimum ? parameter : `${parameter}?`));
    if (variadic) written.push("…");
    return `${name}(${written.join(", ")})`;
}

export type FunctionGroup = { group: string; items: FunctionDescription[] };

/**
 * Functions under their headings, in the order the engine listed them.
 *
 * The same reading as `groupSuggestions`, and for the same reason: the server states the
 * order — logic first because `if` is what a formula field is mostly opened for, dates last
 * because that section is the longest — and re-sorting here would replace an editorial
 * decision with an alphabet. A description carrying no group is collected under a single
 * unnamed run rather than dropped, so a function a host registered stays pickable.
 */
export function groupFunctions(descriptions: FunctionDescription[]): FunctionGroup[] {
    const groups: FunctionGroup[] = [];
    const index = new Map<string, FunctionGroup>();
    for (const description of descriptions) {
        const name = description.group ?? "";
        let group = index.get(name);
        if (!group) {
            group = { group: name, items: [] };
            index.set(name, group);
            groups.push(group);
        }
        group.items.push(description);
    }
    return groups;
}
